import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  fetchAuthSession,
  getCurrentUser,
} from 'aws-amplify/auth';

import clearStoredUserData from '../../utils/common/clearStoredUserData';
import { isAuthLocal } from './utils';

/**
 * Ends the session, then forgets the reader. Amplify revokes with the refresh
 * token it has stored, which the clear removes, so the order is load-bearing; a
 * sign-out it could not complete clears nothing at all.
 */
export const signOut = async () => {
  await amplifySignOut();
  clearStoredUserData();
};

/**
 * @returns The current user's userId
 */
export const getCurrentUserId = async () => {
  try {
    const user = await getCurrentUser();
    return user.userId;
  } catch {
    return undefined;
  }
};

export enum SessionState {
  /** Credentials Cognito recognises. */
  SignedIn = 'signedIn',
  /** No credentials, or credentials Cognito refused. */
  SignedOut = 'signedOut',
  /** Credentials held, which Cognito could not be reached to confirm. */
  Unreadable = 'unreadable',
}

/** Amplify's name for a request that never reached Cognito. */
const NETWORK_ERROR = 'NetworkError';

const isNetworkFailure = (error: unknown) =>
  typeof error === 'object' && error !== null && (error as Error).name === NETWORK_ERROR;

/**
 * Reads the session. Amplify keeps the tokens through a network failure and
 * clears them through every other, so that one leaves the session standing.
 */
export const readSessionState = async (): Promise<SessionState> => {
  try {
    await getCurrentUser();
    return SessionState.SignedIn;
  } catch (error) {
    return isNetworkFailure(error) ? SessionState.Unreadable : SessionState.SignedOut;
  }
};

/**
 * @returns Currently authenticated user's Cognito groups, or `undefined` when
 * the session cannot be read — an unreadable session is not a user who belongs
 * to no groups, and callers cache the result
 */
export const getCurrentAWSCognitoGroups = async () => {
  try {
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      return undefined;
    }

    return (session.tokens.idToken.payload['cognito:groups'] as string[]) || [];
  } catch {
    return undefined;
  }
};

/**
 * @returns Currently authenticated user's access token
 */
export const getAccessToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.accessToken?.toString();
};

/**
 * Creates an unverified AWS Cognito account
 * @param email
 * @param password
 * @param phoneNumber
 */
export const signUp = async (
  email: string | undefined,
  password: string | undefined,
  phoneNumber: string | undefined,
  firstName?: string,
  lastName?: string
) => {
  if (!email || !password || !phoneNumber) {
    throw new Error('Missing required details');
  }

  return amplifySignUp({
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        phone_number: phoneNumber,
        given_name: firstName,
        family_name: lastName,
      },
    },
  });
};

/**
 * Authenticates an AWS Cognito account, then stores it in local storage
 * @param email
 * @param password
 */
export const signIn = async (email: string, password: string) =>
  amplifySignIn({
    username: email,
    password,
    options: {
      // SRP authentication flow is not supported in local mode
      authFlowType: isAuthLocal() ? 'USER_PASSWORD_AUTH' : undefined,
    },
  });
